import type { ConnectError } from '@connectrpc/connect'
import {
  SplitSupplyContractErrorReason,
  SplitSupplyContractErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

const KEY_PREFIX =
  'page:adminOcctoMaster.splitSupplyContract.serverError.reason' as const

/**
 * `SplitSupplyContractErrorReason` → i18n key + 表示先 (欄脇 / 上部 Alert) の registry。
 * `Record<enum, entry | undefined>` で網羅を型で強制する (BgMaster/models/serverError.ts と
 * 同じ契約、 docs/adr/structured-app-error-standard-errorinfo-envelope.md)。
 *
 * field-scoped にするのは reason が常に 1 つの入力欄に帰属するものだけ:
 * 優先順位 (`basePriority`) / 負荷追随分・ベース分の事業者・契約識別番号 1 / 供給形態 / 固定型の契約電力 / 変動型の通告値を出す側。
 * 供給地点の 3 reason は編集では欄が「変更不可」の表示で、 自社の立場・
 * 担当時間帯・基本パターンは複数の欄にまたがるので operation-scoped (上部 Alert)。
 */
const SPLIT_SUPPLY_CONTRACT_REASON_KEYS: Record<
  SplitSupplyContractErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [SplitSupplyContractErrorReason.UNSPECIFIED]: undefined,
  [SplitSupplyContractErrorReason.SUPPLY_POINT_REQUIRED]: {
    key: `${KEY_PREFIX}.supplyPointRequired`,
  },
  [SplitSupplyContractErrorReason.SUPPLY_POINT_NOT_ELIGIBLE]: {
    key: `${KEY_PREFIX}.supplyPointNotEligible`,
  },
  [SplitSupplyContractErrorReason.LOAD_FOLLOWING_SUPPLIER_REQUIRED]: {
    key: `${KEY_PREFIX}.loadFollowingSupplierRequired`,
    field: 'loadFollowingBusinessMasterId',
  },
  [SplitSupplyContractErrorReason.BASE_SUPPLIER_REQUIRED]: {
    key: `${KEY_PREFIX}.baseSupplierRequired`,
    field: 'baseBusinessMasterId',
  },
  [SplitSupplyContractErrorReason.SELF_ROLE_UNDETERMINED]: {
    key: `${KEY_PREFIX}.selfRoleUndetermined`,
  },
  [SplitSupplyContractErrorReason.SUPPLY_FORM_REQUIRED]: {
    key: `${KEY_PREFIX}.supplyFormRequired`,
    field: 'supplyForm',
  },
  [SplitSupplyContractErrorReason.OWN_SLOT_INVALID]: {
    key: `${KEY_PREFIX}.ownSlotInvalid`,
  },
  [SplitSupplyContractErrorReason.SUPPLY_POINT_DUPLICATED]: {
    key: `${KEY_PREFIX}.supplyPointDuplicated`,
  },
  [SplitSupplyContractErrorReason.PRIORITY_OUT_OF_RANGE]: {
    key: `${KEY_PREFIX}.priorityOutOfRange`,
    field: 'basePriority',
  },
  [SplitSupplyContractErrorReason.PARTNER_NOT_ELIGIBLE]: {
    key: `${KEY_PREFIX}.partnerNotEligible`,
  },
  [SplitSupplyContractErrorReason.OWN_SUPPLIER_NOT_ELIGIBLE]: {
    key: `${KEY_PREFIX}.ownSupplierNotEligible`,
  },
  [SplitSupplyContractErrorReason.PATTERN_ORDER_DUPLICATED]: {
    key: `${KEY_PREFIX}.patternOrderDuplicated`,
  },
  [SplitSupplyContractErrorReason.PATTERN_DAY_TYPES_REQUIRED]: {
    key: `${KEY_PREFIX}.patternDayTypesRequired`,
  },
  [SplitSupplyContractErrorReason.PATTERN_PERIOD_INVALID]: {
    key: `${KEY_PREFIX}.patternPeriodInvalid`,
  },
  [SplitSupplyContractErrorReason.PATTERN_VALUES_INVALID]: {
    key: `${KEY_PREFIX}.patternValuesInvalid`,
  },
  [SplitSupplyContractErrorReason.PATTERN_SLOT_RANGE_INVALID]: {
    key: `${KEY_PREFIX}.patternSlotRangeInvalid`,
  },
  [SplitSupplyContractErrorReason.BASE_CONTRACT_KW_REQUIRED]: {
    key: `${KEY_PREFIX}.baseContractKwRequired`,
    field: 'baseContractKw',
  },
  [SplitSupplyContractErrorReason.NOTICE_BY_REQUIRED]: {
    key: `${KEY_PREFIX}.noticeByRequired`,
    field: 'noticeBy',
  },
  [SplitSupplyContractErrorReason.LOAD_FOLLOWING_CONTRACT_ID_REQUIRED]: {
    key: `${KEY_PREFIX}.loadFollowingContractIdRequired`,
    field: 'loadFollowingContractId',
  },
  [SplitSupplyContractErrorReason.BASE_CONTRACT_ID_REQUIRED]: {
    key: `${KEY_PREFIX}.baseContractIdRequired`,
    field: 'baseContractId',
  },
}

/**
 * Create / Update の `useCrudModalForm` 用 `resolveDetailedError`。 `google.rpc.ErrorInfo`
 * detail を registry で文言と表示先に解決する。 detail が無い・未知の reason は
 * `undefined` で、 呼び出し側の code ベースの文言に落ちる。
 */
export function resolveSplitSupplyContractDetailedError(
  err: ConnectError,
  t: TFunction,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    SplitSupplyContractErrorReasonSchema,
    SPLIT_SUPPLY_CONTRACT_REASON_KEYS,
    t,
    {},
  )
}
